import {
  CAMPAIGN_STATUSES,
  CAMPAIGN_TYPES,
  CampaignMemberKind,
  CampaignMemberSource,
  CampaignMemberStatus,
  CampaignType,
} from '../../interfaces/campaign'
import { FilterFieldType, IFilter } from './types'

const toOptions = (values: readonly string[]) => values.map((value) => ({ value, label: value }))

export const CAMPAIGNS_LIST_FILTERS_CONFIG = [
  {
    field: 'name',
    alias: 'campaign',
    type: FilterFieldType.Text,
    title: 'Campaign',
    placeholder: 'Campaign',
    isSortable: true,
  },
  {
    field: 'type',
    alias: 'campaign',
    type: FilterFieldType.List,
    isMultiple: true,
    title: 'Type',
    placeholder: 'Type',
    isSortable: true,
    options: toOptions(CAMPAIGN_TYPES),
  },
  {
    field: 'status',
    alias: 'campaign',
    type: FilterFieldType.List,
    isMultiple: true,
    title: 'Status',
    placeholder: 'Status',
    isSortable: true,
    options: toOptions(CAMPAIGN_STATUSES),
  },
  {
    field: 'firstName',
    alias: 'owner',
    type: FilterFieldType.Text,
    title: 'Owner',
    isSortable: true,
    excludeFilter: true,
  },
  {
    // Options injected client-side from the users list, as on the other CRM tabs.
    field: 'ownerId',
    dbField: 'owner_id',
    alias: 'campaign',
    type: FilterFieldType.Autocomplete,
    isMultiple: true,
    title: 'Owner',
    placeholder: 'Owner',
    excludeColumn: true,
  },
  {
    field: 'startDate',
    alias: 'campaign',
    type: FilterFieldType.Date,
    title: 'Start',
    align: 'right',
    isSortable: true,
    isExternalField: true,
  },
  {
    field: 'endDate',
    alias: 'campaign',
    type: FilterFieldType.Date,
    title: 'End',
    align: 'right',
    isSortable: true,
  },
  {
    field: 'memberCount',
    type: FilterFieldType.Number,
    title: 'Members',
    align: 'right',
    isCustom: true,
    isSortable: true,
    excludeFilter: true,
  },
  {
    // Expenses arrive monthly from Salesforce, so this can lag by up to a month.
    field: 'spend',
    type: FilterFieldType.Amount,
    title: 'Spend',
    align: 'right',
    isCustom: true,
    isSortable: true,
    excludeFilter: true,
  },
  {
    field: 'isActive',
    alias: 'campaign',
    type: FilterFieldType.Boolean,
    title: 'Active',
    excludeColumn: true,
  },
  {
    // System campaigns are hidden unless this is on or the Type filter names System.
    field: 'includeSystem',
    type: FilterFieldType.Boolean,
    title: 'Include system campaigns',
    isCustom: true,
    excludeColumn: true,
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [
      { title: 'Active', filters: { isActive: 'true' } },
      {
        title: 'Events',
        filters: { type: [CampaignType.HostedEvent, CampaignType.ConferenceTradeShow] },
      },
      { title: 'Outreach', filters: { type: [CampaignType.OutreachList, CampaignType.BdTrip] } },
      { title: 'Sends', filters: { type: [CampaignType.MarketingSend] } },
    ],
  },
] as const satisfies IFilter[]

// Filtered in memory over the campaign's members (see filterNetworkRows).
export const CAMPAIGN_MEMBERS_FILTERS_CONFIG = [
  {
    field: 'name',
    type: FilterFieldType.Text,
    title: 'Member',
    placeholder: 'Member',
    isSortable: true,
  },
  {
    field: 'kind',
    type: FilterFieldType.List,
    isMultiple: true,
    title: 'Type',
    placeholder: 'Type',
    align: 'left',
    isSortable: true,
    options: toOptions(Object.values(CampaignMemberKind)),
  },
  {
    field: 'affiliation',
    type: FilterFieldType.Text,
    title: 'Company / Org',
    placeholder: 'Company / Org',
    isSortable: true,
  },
  {
    field: 'connectedCompanies',
    type: FilterFieldType.Empty,
    title: 'Connected deals',
  },
  {
    field: 'status',
    type: FilterFieldType.List,
    isMultiple: true,
    title: 'Status',
    placeholder: 'Status',
    align: 'left',
    isSortable: true,
    options: toOptions(Object.values(CampaignMemberStatus)),
  },
  {
    field: 'ownerName',
    type: FilterFieldType.Text,
    title: 'Owner',
    placeholder: 'Owner',
    isSortable: true,
  },
  {
    field: 'joinedAt',
    type: FilterFieldType.Date,
    title: 'Added',
    align: 'right',
    isSortable: true,
  },
  {
    field: 'lastActivityDate',
    type: FilterFieldType.Date,
    title: 'Last activity',
    align: 'right',
    isSortable: true,
  },
  {
    field: 'notes',
    type: FilterFieldType.Text,
    title: 'Notes',
    placeholder: 'Notes',
  },
  {
    field: 'action',
    type: FilterFieldType.Empty,
    title: 'Action',
    align: 'right',
  },
  {
    field: 'source',
    type: FilterFieldType.List,
    isMultiple: true,
    title: 'Added via',
    options: toOptions(Object.values(CampaignMemberSource)),
    excludeColumn: true,
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [
      // A marketing send's page opens here: the people who clicked are the ones worth a follow-up.
      { title: 'Clicked', filters: { status: [CampaignMemberStatus.Clicked] } },
    ],
  },
] as const satisfies IFilter[]
